<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue';
import { useAbortableRequest } from 'dashboard/composables/useAbortableRequest';
import { suggestArticles } from '../api/suggest';

const props = defineProps({
  id: { type: String, required: true },
  action: { type: String, required: true },
  suggestUrl: { type: String, required: true },
  locale: { type: String, required: true },
  label: { type: String, required: true },
  placeholder: { type: String, required: true },
  strings: { type: Object, required: true },
  initialQuery: { type: String, default: '' },
  size: { type: String, default: 'compact' },
});

const DEBOUNCE_MS = 250;
const SUPERSEDED = Symbol('superseded');
const APPLE_PLATFORM = /Mac|iPhone|iPad|iPod/i;

const root = ref(null);
const input = ref(null);
const term = ref(props.initialQuery);
const results = ref([]);
const resultsFor = ref('');
const status = ref('idle');
const expanded = ref(false);
const focused = ref(false);
const activeIndex = ref(-1);
const { run, abort } = useAbortableRequest();
let timer;

const inputId = `${props.id}-input`;
const listboxId = `${props.id}-listbox`;
const large = props.size === 'large';
const pluralRules = new Intl.PluralRules(props.locale.replace('_', '-'));
const shortcutKeys = APPLE_PLATFORM.test(
  navigator.platform || navigator.userAgent
)
  ? '⌘K'
  : 'Ctrl K';

const interpolate = (template, values) =>
  template.replace(/%\{(\w+)\}/g, (_, key) => values[key]);

const query = computed(() => term.value.trim());
const searchHref = value =>
  `${props.action}?${new URLSearchParams({ query: value })}`;

const options = computed(() => {
  if (!results.value.length) return [];
  return [
    ...results.value.map((article, index) => ({
      id: `${props.id}-option-${index}`,
      href: article.link,
      article,
    })),
    {
      id: `${props.id}-option-all`,
      href: searchHref(resultsFor.value),
      label: interpolate(props.strings.see_all, { query: resultsFor.value }),
    },
  ];
});

const isOpen = computed(
  () => expanded.value && query.value !== '' && status.value !== 'idle'
);
const activeOption = computed(() =>
  isOpen.value ? options.value[activeIndex.value] : undefined
);
const isLoading = computed(() => status.value === 'loading');

const message = computed(() => {
  if (status.value === 'error') return props.strings.error;
  if (results.value.length) return '';
  if (isLoading.value) return props.strings.loading;
  return interpolate(props.strings.empty, { query: resultsFor.value });
});

const announcement = computed(() => {
  if (!isOpen.value || isLoading.value) return '';
  if (status.value === 'error' || !results.value.length) return message.value;
  const count = results.value.length;
  const forms = props.strings.count;
  return interpolate(forms[pluralRules.select(count)] || forms.other, {
    count,
  });
});

const close = () => {
  expanded.value = false;
  activeIndex.value = -1;
};

const fetchSuggestions = async value => {
  try {
    const articles = await run(
      signal => suggestArticles(props.suggestUrl, value, { signal }),
      { onAbort: SUPERSEDED }
    );
    if (articles === SUPERSEDED) return;
    results.value = articles;
    resultsFor.value = value;
    status.value = 'ready';
  } catch {
    results.value = [];
    status.value = 'error';
  }
};

watch(query, value => {
  clearTimeout(timer);
  activeIndex.value = -1;
  if (!value) {
    abort();
    results.value = [];
    status.value = 'idle';
    close();
    return;
  }
  status.value = 'loading';
  expanded.value = true;
  timer = setTimeout(() => fetchSuggestions(value), DEBOUNCE_MS);
});

watch(activeIndex, index => {
  const option = options.value[index];
  if (option)
    nextTick(() =>
      document.getElementById(option.id).scrollIntoView({ block: 'nearest' })
    );
});

onBeforeUnmount(() => clearTimeout(timer));

const visit = href => {
  if (window.Turbo) window.Turbo.visit(href);
  else window.location.assign(href);
};

const move = step => {
  if (!query.value) return;
  expanded.value = true;
  const count = options.value.length;
  if (!isOpen.value || !count) return;
  if (activeIndex.value === -1) activeIndex.value = step > 0 ? 0 : count - 1;
  else activeIndex.value = (activeIndex.value + step + count) % count;
};

const onEnter = event => {
  const option = activeOption.value;
  if (option) {
    event.preventDefault();
    close();
    visit(option.href);
  } else if (!query.value) {
    event.preventDefault();
  }
};

const onEscape = event => {
  if (isOpen.value) {
    event.preventDefault();
    close();
  } else if (term.value) {
    event.preventDefault();
    term.value = '';
  }
};

const onKeydown = event => {
  if (event.isComposing) return;
  if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
    event.preventDefault();
    move(event.key === 'ArrowDown' ? 1 : -1);
  } else if (event.key === 'Enter') {
    onEnter(event);
  } else if (event.key === 'Escape') {
    onEscape(event);
  } else if (event.key === 'Tab') {
    close();
  }
};

const onFocus = () => {
  focused.value = true;
  if (query.value && status.value !== 'idle') expanded.value = true;
};

const onFocusout = event => {
  if (root.value.contains(event.relatedTarget)) return;
  focused.value = false;
  close();
};

const onSubmit = event => {
  if (!query.value) event.preventDefault();
  close();
};

const clear = () => {
  term.value = '';
  input.value.focus();
};
</script>

<template>
  <div ref="root" class="relative w-full" @focusout="onFocusout">
    <form
      :action="action"
      method="get"
      role="search"
      :aria-label="label"
      class="relative"
      @submit="onSubmit"
    >
      <label :for="inputId" class="sr-only">{{ label }}</label>
      <span
        class="i-lucide-search pointer-events-none absolute top-1/2 -translate-y-1/2 text-hc-text-muted"
        :class="large ? 'start-4 size-5' : 'start-3 size-4'"
        aria-hidden="true"
      />
      <input
        :id="inputId"
        ref="input"
        v-model="term"
        type="search"
        name="query"
        role="combobox"
        autocomplete="off"
        autocapitalize="off"
        spellcheck="false"
        enterkeyhint="search"
        aria-autocomplete="list"
        :aria-expanded="String(isOpen)"
        :aria-controls="listboxId"
        :aria-activedescendant="activeOption?.id"
        aria-keyshortcuts="Meta+K Control+K"
        :placeholder="placeholder"
        class="w-full appearance-none rounded-hc border border-hc-border bg-hc-surface text-hc-text shadow-sm transition-[border-color,box-shadow] placeholder:text-hc-text-muted hover:border-hc-text-muted/50 focus:border-hc-link focus:outline-none focus:ring-4 focus:ring-hc-link/15 [&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none"
        :class="
          large ? 'h-14 pe-12 ps-12 text-base' : 'h-10 pe-10 ps-9 text-sm'
        "
        @keydown="onKeydown"
        @focus="onFocus"
      />
      <span
        class="pointer-events-none absolute inset-y-0 flex items-center"
        :class="large ? 'end-4' : 'end-3'"
      >
        <span
          v-if="isLoading"
          class="i-lucide-loader-circle size-4 animate-spin text-hc-text-muted"
          aria-hidden="true"
        />
        <button
          v-else-if="term"
          type="button"
          class="pointer-events-auto -me-1.5 grid size-7 place-items-center rounded-hc-sm text-hc-text-muted transition-colors hover:bg-hc-surface-raised hover:text-hc-heading"
          :aria-label="strings.clear"
          @click="clear"
        >
          <span class="i-lucide-x size-4" aria-hidden="true" />
        </button>
        <kbd
          v-else-if="!focused"
          class="rounded-hc-sm border border-hc-border bg-hc-bg px-1.5 py-0.5 font-hc-body text-[0.6875rem] font-medium leading-none text-hc-text-muted max-md:hidden"
          aria-hidden="true"
        >
          {{ shortcutKeys }}
        </kbd>
      </span>
    </form>
    <div
      v-show="isOpen"
      class="absolute top-full z-50 mt-2 overflow-hidden rounded-hc-lg border border-hc-border bg-hc-surface text-start shadow-xl shadow-black/5 dark:shadow-black/40"
      :class="
        large
          ? 'inset-x-0'
          : 'end-0 w-full md:w-[30rem] md:max-w-[calc(100vw-2rem)]'
      "
      @mousedown.prevent
    >
      <div
        :id="listboxId"
        role="listbox"
        :aria-label="strings.label"
        class="max-h-[min(28rem,70vh)] overflow-y-auto overscroll-contain p-1.5"
        :class="{ hidden: !options.length }"
      >
        <a
          v-for="(option, index) in options"
          :id="option.id"
          :key="option.id"
          role="option"
          :href="option.href"
          tabindex="-1"
          :aria-selected="String(index === activeIndex)"
          class="group flex items-start gap-3 rounded-hc px-3 py-2.5 text-hc-text aria-selected:bg-hc-surface-raised aria-selected:ring-1 aria-selected:ring-inset aria-selected:ring-hc-border"
          @mousemove="activeIndex = index"
        >
          <template v-if="option.article">
            <span
              class="i-lucide-file-text mt-0.5 size-4 shrink-0 text-hc-text-muted"
              aria-hidden="true"
            />
            <span class="min-w-0 flex-1">
              <span
                v-if="option.article.category"
                class="block truncate text-xs text-hc-text-muted"
              >
                {{ option.article.category }}
              </span>
              <span
                class="block truncate text-sm font-semibold text-hc-heading"
              >
                {{ option.article.title }}
              </span>
              <span
                v-dompurify-html="option.article.snippet"
                class="mt-0.5 line-clamp-2 text-[0.8125rem] leading-5 text-hc-text-muted [&_mark]:rounded-sm [&_mark]:bg-hc-mark-bg [&_mark]:px-0.5 [&_mark]:font-semibold [&_mark]:text-hc-heading"
              />
            </span>
          </template>
          <template v-else>
            <span
              class="i-lucide-search mt-0.5 size-4 shrink-0 text-hc-link"
              aria-hidden="true"
            />
            <span
              class="min-w-0 flex-1 truncate text-sm font-medium text-hc-link"
            >
              {{ option.label }}
            </span>
          </template>
          <span
            class="i-lucide-corner-down-left mt-0.5 size-4 shrink-0 text-hc-text-muted opacity-0 group-aria-selected:opacity-100 rtl:-scale-x-100"
            aria-hidden="true"
          />
        </a>
      </div>
      <p
        v-if="message"
        class="px-4 py-6 text-center text-sm text-hc-text-muted"
      >
        {{ message }}
      </p>
      <div
        v-if="options.length"
        class="flex items-center gap-4 border-t border-hc-border bg-hc-bg/60 px-4 py-2 text-xs text-hc-text-muted max-md:hidden"
        aria-hidden="true"
      >
        <span class="flex items-center gap-1.5">
          <kbd
            class="grid size-5 place-items-center rounded-hc-sm border border-hc-border bg-hc-surface"
          >
            <span class="i-lucide-arrow-up size-3" />
          </kbd>
          <kbd
            class="grid size-5 place-items-center rounded-hc-sm border border-hc-border bg-hc-surface"
          >
            <span class="i-lucide-arrow-down size-3" />
          </kbd>
          {{ strings.navigate }}
        </span>
        <span class="flex items-center gap-1.5">
          <kbd
            class="grid size-5 place-items-center rounded-hc-sm border border-hc-border bg-hc-surface"
          >
            <span class="i-lucide-corner-down-left size-3 rtl:-scale-x-100" />
          </kbd>
          {{ strings.open }}
        </span>
        <span class="flex items-center gap-1.5">
          <kbd
            class="rounded-hc-sm border border-hc-border bg-hc-surface px-1 font-hc-body text-[0.625rem] leading-4"
          >
            {{ strings.escape_key }}
          </kbd>
          {{ strings.close }}
        </span>
      </div>
    </div>
    <p role="status" class="sr-only">{{ announcement }}</p>
  </div>
</template>
